import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { Droppable } from '@hello-pangea/dnd';
import { ExperienceResourceArray, ProductCollectionResource } from '../../../Types';
import ExperienceSection from '../../ExperienceSection';
import NoExperiences from '../../NoExperiences';
import ProductCollectionDisplay from '../ProductCollectionDisplay';
import { useUnifiedDragContext } from '../UnifiedDragContext';
import DraggableListItem from './DraggableListItem';
import EmptyDropZone from '../Shared/EmptyDropZone';
import { getDragHighlightClass } from '../Shared/styles/dragStyles';
import { useExperienceMap } from '../Shared/hooks/useExperienceMap';

export interface Props {
  productCollection: ProductCollectionResource;
  experiencesArray: ExperienceResourceArray;
  index: number;
  totalSections: number;
  onOpenCatalog: () => void;
  editable?: boolean;
}

function List({
  productCollection,
  experiencesArray,
  index,
  totalSections,
  onOpenCatalog,
  editable = true,
}: Props) {
  const { experiences } = productCollection.payload;
  const { isDragging } = useUnifiedDragContext();

  const experienceMap = useExperienceMap(experiencesArray);

  return (
    <ExperienceSection
      productCollection={productCollection}
      index={index}
      totalSections={totalSections}
      onOpenCatalog={onOpenCatalog}
      editable={editable}
    >
      <ProductCollectionDisplay productCollection={productCollection}>
        <Droppable droppableId={`section-${index}-list`} direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`mt-6 sm:mt-14 transition-colors ${
                experiences.length > 0
                  ? `flex flex-col gap-3 ${getDragHighlightClass(snapshot.isDraggingOver)}`
                  : ''
              }`}
            >
              {experiences.length > 0 ? (
                <>
                  {experiences.map((experienceId, idx) => (
                    <DraggableListItem
                      key={experienceId}
                      experienceResource={fromNullable(experienceMap[experienceId])}
                      experienceId={experienceId}
                      index={idx}
                      sectionIndex={index}
                      isDragging={isDragging}
                      editable={editable}
                    />
                  ))}
                </>
              ) : isDragging ? (
                <EmptyDropZone isDraggingOver={snapshot.isDraggingOver} />
              ) : (
                <NoExperiences onOpen={onOpenCatalog} />
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </ProductCollectionDisplay>
    </ExperienceSection>
  );
}

export default observer(List);
